import { useEffect, useRef, useState } from "react";
import { formatDisplayName } from "../utils/displayName";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  Save,
  Trash2,
  X,
} from "lucide-react";
import AdminDashboardLayout from "../components/AdminDashboardLayout";
import PortalFeedback from "../components/PortalFeedback";
import {
  contactPreferenceOptions,
  contactRoleOptions,
  PortalSelect,
  PortalServicesDropdown,
  priorityOptions,
} from "../components/PortalDropdown";
import { supabase } from "../supabaseClient";
import { formatBrokerChangeDiffs } from "../utils/brokerChangeDiff";
import { PROJECT_STATUSES, normalizeProjectStatus, toProjectStatusDbValue } from "../utils/projectStatus";
import { formatExpectedTiming } from "../utils/timeFormat";

const PANEL =
  "rounded-2xl border border-white/15 bg-white/70 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

const assignedPilotOptions = ["Ray Tisdale", "Harry Carvel", "Lily Paul"];
const normalizeStatus = (status) => normalizeProjectStatus(status) || "Requested";
const DEFAULT_TIMEZONE = "America/New_York";
const CHECKLIST_ITEMS = [
  {
    key: "interior_shoot_complete",
    label: "Interior shoot complete",
    notifies: "Internal",
  },
  {
    key: "exterior_shoot_complete",
    label: "Exterior shoot complete",
    notifies: "Internal",
  },
  {
    key: "logs_wb_poh_captured",
    label: "Logs / W&B from POH captured",
    notifies: "Internal",
  },
];
const SHOOT_CHECKLIST_KEYS = ["interior_shoot_complete", "exterior_shoot_complete", "logs_wb_poh_captured"];
const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Phoenix", label: "Arizona" },
  { value: "America/Los_Angeles", label: "Pacific" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
];
const TIME_OPTIONS = Array.from({ length: (20 - 7) * 4 + 1 }, (_, index) => {
  const totalMinutes = 7 * 60 + index * 15;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  const label = new Date(`2026-01-01T${value}:00`).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  return { value, label };
});
const DEFAULT_TIME = "09:00";
const normalizeTimeOption = (time) =>
  TIME_OPTIONS.some((option) => option.value === time) ? time : DEFAULT_TIME;

const splitServices = (value = "") =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
const getContactPreferenceLabel = (value) =>
  contactPreferenceOptions.find((option) => option.value === value)?.label || value || "—";
const getDialablePhone = (value = "") => {
  const digits = String(value).replace(/\D/g, "");
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith("1")) return digits;
  return "";
};
const getMailtoEmail = (value = "") => {
  const trimmed = String(value).trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed) ? trimmed : "";
};
const formatHistoryExpectedTiming = (item, timeZone) => {
  const oldTiming = formatExpectedTiming(item.old_expected_datetime, timeZone);
  const newTiming = formatExpectedTiming(item.new_expected_datetime, timeZone);

  if (oldTiming === "—" && newTiming === "—") return "";
  if (oldTiming === newTiming || oldTiming === "—") return newTiming;
  if (newTiming === "—") return `Cleared from ${oldTiming}`;
  return `${oldTiming} → ${newTiming}`;
};
const formatStatusTransition = (item) => {
  if (!item.old_status || !item.new_status) return "Project updated";
  return `${normalizeStatus(item.old_status)} → ${normalizeStatus(item.new_status)}`;
};
const getTimeZoneParts = (date, timeZone) =>
  new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(date)
    .reduce((parts, part) => {
      if (part.type !== "literal") parts[part.type] = part.value;
      return parts;
    }, {});

const getTimeZoneOffsetMs = (date, timeZone) => {
  const parts = getTimeZoneParts(date, timeZone);
  const hour = parts.hour === "24" ? "00" : parts.hour;
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return asUTC - date.getTime();
};

const zonedInputToUtcIso = (value, timeZone) => {
  if (!value) return "";
  const [datePart, timePart] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
  const offset = getTimeZoneOffsetMs(utcGuess, timeZone || DEFAULT_TIMEZONE);
  return new Date(utcGuess.getTime() - offset).toISOString();
};

const toInputDateTime = (value, timeZone = DEFAULT_TIMEZONE) => {
  if (!value) return "";
  const parts = getTimeZoneParts(new Date(value), timeZone);
  const hour = parts.hour === "24" ? "00" : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
};

const getDatePart = (value) => (value ? value.slice(0, 10) : "");
const getTimePart = (value) => (value ? value.slice(11, 16) : "");
const combineDateTime = (date, time) => (date ? `${date}T${time || DEFAULT_TIME}` : "");
const toLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const formatDateDisplay = (dateKey) => {
  if (!dateKey) return "Select date";
  return new Date(`${dateKey}T00:00:00`).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};
const deriveChecklistCompleted = (itemKey, status) => {
  const normalizedStatus = normalizeStatus(status);
  const statusIndex = PROJECT_STATUSES.indexOf(normalizedStatus);
  const productionIndex = PROJECT_STATUSES.indexOf("Production");

  if (SHOOT_CHECKLIST_KEYS.includes(itemKey)) {
    return statusIndex >= productionIndex;
  }

  return false;
};
const hasChecklistChanges = (item) =>
  item.completed !== item.savedCompleted || item.notes.trim() !== item.savedNotes.trim();
const buildChecklistRows = (rows = [], status = "Requested") => {
  const byKey = new Map(rows.map((row) => [row.item_key, row]));
  return CHECKLIST_ITEMS.map((item) => {
    const row = byKey.get(item.key);
    const completed = row ? !!row.completed || deriveChecklistCompleted(item.key, status) : deriveChecklistCompleted(item.key, status);
    const notes = row?.notes || "";
    return {
      ...item,
      id: row?.id || "",
      completed,
      notes,
      savedCompleted: completed,
      savedNotes: notes,
      completed_at: row?.completed_at || "",
      completed_by: row?.completed_by || "",
      derivedFromStatus: completed && !row?.completed_at,
    };
  });
};
const applyStatusChecklistDefaults = (items, status) =>
  items.map((item) => {
    const impliedCompleted = deriveChecklistCompleted(item.key, status);

    if (impliedCompleted) {
      return {
        ...item,
        completed: true,
        savedCompleted: true,
        derivedFromStatus: !item.completed_at,
      };
    }

    if (item.derivedFromStatus && !item.id) {
      return {
        ...item,
        completed: false,
        savedCompleted: false,
        derivedFromStatus: false,
      };
    }

    return item;
  });
const getCalendarDays = (monthDate) => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const next = new Date(gridStart);
    next.setDate(gridStart.getDate() + index);
    return next;
  });
};

export default function AdminProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [history, setHistory] = useState([]);
  const [changeLog, setChangeLog] = useState([]);
  const [checklist, setChecklist] = useState(() => buildChecklistRows());
  const [formData, setFormData] = useState({
    current_status: "Requested",
    expected_datetime: "",
    timezone: DEFAULT_TIMEZONE,
    assigned_pilot: "",
    notes: "",
  });
  const [requestForm, setRequestForm] = useState({
    tail_number: "",
    priority: "Medium",
    services: [],
    airport_identifier: "",
    contact_name: "",
    contact_role: "",
    contact_phone: "",
    contact_email: "",
    contact_method_preference: "no preference",
    notes: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [errorText, setErrorText] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [requestEditing, setRequestEditing] = useState(false);
  const [phoneActionsOpen, setPhoneActionsOpen] = useState(false);
  const [savingChecklistKey, setSavingChecklistKey] = useState("");
  const phoneActionsRef = useRef(null);
  const listPath = location.state?.from || "/admin/projects";

  const loadProject = async () => {
    setLoading(true);
    setErrorText("");

    const { data, error } = await supabase
      .from("project_status")
      .select("id,intake_id,tail_number,current_status,priority,assigned_pilot,expected_datetime,timezone,notes,broker_intake(id,created_at,services,tail_number,priority,airport_identifier,contact_name,contact_phone,contact_email,contact_method_preference,contact_role,notes,broker_accounts(name))")
      .eq("id", id)
      .maybeSingle();

    if (error || !data) {
      setErrorText(error?.message || "Project not found.");
      setProject(null);
      setLoading(false);
      return;
    }

    setProject(data);
    const projectTimezone = data.timezone || DEFAULT_TIMEZONE;
    setFormData({
      current_status: normalizeStatus(data.current_status),
      expected_datetime: toInputDateTime(data.expected_datetime, projectTimezone),
      timezone: projectTimezone,
      assigned_pilot: data.assigned_pilot || "",
      notes: data.notes || "",
    });
    setRequestForm({
      tail_number: data.broker_intake?.tail_number || data.tail_number || "",
      priority: data.broker_intake?.priority || data.priority || "Medium",
      services: splitServices(data.broker_intake?.services || ""),
      airport_identifier: data.broker_intake?.airport_identifier || "",
      contact_name: data.broker_intake?.contact_name || "",
      contact_role: data.broker_intake?.contact_role || "",
      contact_phone: data.broker_intake?.contact_phone || "",
      contact_email: data.broker_intake?.contact_email || "",
      contact_method_preference: data.broker_intake?.contact_method_preference || "no preference",
      notes: data.broker_intake?.notes || "",
    });
    setRequestEditing(false);

    const { data: historyRows } = await supabase
      .from("project_status_history")
      .select("*")
      .eq("project_status_id", data.id)
      .order("created_at", { ascending: false })
      .limit(10);
    setHistory(historyRows || []);

    const { data: changeRows } = await supabase
      .from("broker_project_change_log")
      .select("id, change_type, old_values, new_values, actor_type, actor_name, actor_email, created_at")
      .eq("intake_id", data.broker_intake?.id)
      .order("created_at", { ascending: false })
      .limit(8);
    setChangeLog(changeRows || []);

    const { data: checklistRows } = await supabase
      .from("project_internal_checklist")
      .select("id,item_key,completed,notes,completed_at,completed_by")
      .eq("project_status_id", data.id);
    setChecklist(buildChecklistRows(checklistRows || [], data.current_status));
    setLoading(false);
  };

  useEffect(() => {
    loadProject();
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    setErrorText("");
    setMessage("");

    const { error } = await supabase
      .from("project_status")
      .update({
        current_status: toProjectStatusDbValue(formData.current_status),
        expected_datetime: formData.expected_datetime
          ? zonedInputToUtcIso(
              combineDateTime(
                getDatePart(formData.expected_datetime),
                normalizeTimeOption(getTimePart(formData.expected_datetime))
              ),
              formData.timezone
            )
          : null,
        timezone: formData.timezone || DEFAULT_TIMEZONE,
        assigned_pilot: formData.assigned_pilot.trim() || null,
        notes: formData.notes.trim() || null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    setSaving(false);

    if (error) {
      setErrorText(error.message || "Could not save project.");
      return;
    }

    setMessage("Project updated. Status changes are logged and queued for broker notification.");
    await loadProject();
  };

  const handleStatusChange = (nextStatus) => {
    setFormData((current) => ({ ...current, current_status: nextStatus }));
    setChecklist((items) => applyStatusChecklistDefaults(items, nextStatus));
  };

  const updateChecklistItem = (itemKey, patch = {}) => {
    setChecklist((items) => items.map((item) => (
      item.key === itemKey ? { ...item, ...patch, derivedFromStatus: false } : item
    )));
  };

  const handleSaveChecklistItem = async (itemKey) => {
    if (!project?.id) return;

    const current = checklist.find((item) => item.key === itemKey);
    if (!current) return;

    setSavingChecklistKey(itemKey);
    setErrorText("");
    setMessage("");

    const { error } = await supabase
      .from("project_internal_checklist")
      .upsert(
        {
          project_status_id: project.id,
          intake_id: project.intake_id || project.broker_intake?.id || null,
          item_key: itemKey,
          completed: !!current.completed,
          notes: current.notes.trim() || null,
        },
        { onConflict: "project_status_id,item_key" }
      );

    setSavingChecklistKey("");

    if (error) {
      setChecklist((items) => items.map((item) => (item.key === itemKey ? current : item)));
      setErrorText(error.message || "Could not save checklist item.");
      return;
    }

    setMessage("Checklist item saved.");
    await loadProject();
  };

  const handleSaveBrokerRequest = async () => {
    if (!project?.broker_intake?.id) return;

    if (!requestForm.tail_number.trim()) {
      setErrorText("Tail number is required.");
      return;
    }
    if (!requestForm.priority) {
      setErrorText("Priority is required.");
      return;
    }
    if (requestForm.services.length === 0) {
      setErrorText("Select at least one service.");
      return;
    }
    if (!requestForm.contact_name.trim()) {
      setErrorText("Point of contact is required.");
      return;
    }
    if (!requestForm.contact_role) {
      setErrorText("Contact role is required.");
      return;
    }
    if (!requestForm.contact_phone.trim() && !requestForm.contact_email.trim()) {
      setErrorText("Enter a point of contact phone or email.");
      return;
    }

    setSaving(true);
    setErrorText("");
    setMessage("");

    const { error } = await supabase
      .from("broker_intake")
      .update({
        tail_number: requestForm.tail_number.trim().toUpperCase(),
        priority: requestForm.priority,
        services: requestForm.services.join(", "),
        airport_identifier: requestForm.airport_identifier.trim().toUpperCase() || null,
        contact_name: requestForm.contact_name.trim() || null,
        contact_role: requestForm.contact_role || null,
        contact_phone: requestForm.contact_phone.trim() || null,
        contact_email: requestForm.contact_email.trim().toLowerCase() || null,
        contact_method_preference: requestForm.contact_method_preference,
        notes: requestForm.notes.trim() || null,
      })
      .eq("id", project.broker_intake.id);

    setSaving(false);

    if (error) {
      setErrorText(error.message || "Could not save broker request changes.");
      return;
    }

    setMessage("Broker request updated. The change was saved to this project record.");
    setRequestEditing(false);
    await loadProject();
  };

  const resetRequestForm = () => {
    setRequestForm({
      tail_number: project?.broker_intake?.tail_number || project?.tail_number || "",
      priority: project?.broker_intake?.priority || project?.priority || "Medium",
      services: splitServices(project?.broker_intake?.services || ""),
      airport_identifier: project?.broker_intake?.airport_identifier || "",
      contact_name: project?.broker_intake?.contact_name || "",
      contact_role: project?.broker_intake?.contact_role || "",
      contact_phone: project?.broker_intake?.contact_phone || "",
      contact_email: project?.broker_intake?.contact_email || "",
      contact_method_preference: project?.broker_intake?.contact_method_preference || "no preference",
      notes: project?.broker_intake?.notes || "",
    });
  };

  const toggleRequestEditing = () => {
    setPhoneActionsOpen(false);
    if (requestEditing) resetRequestForm();
    setRequestEditing((current) => !current);
    setErrorText("");
    setMessage("");
  };

  useEffect(() => {
    if (!phoneActionsOpen) return undefined;

    const handlePointerDown = (event) => {
      if (phoneActionsRef.current?.contains(event.target)) return;
      setPhoneActionsOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [phoneActionsOpen]);

  const openCalendar = () => {
    const datePart = getDatePart(formData.expected_datetime);
    setCalendarMonth(datePart ? new Date(`${datePart}T00:00:00`) : new Date());
    setCalendarOpen((current) => !current);
  };

  const setExpectedDate = (dateKey) => {
    const currentTime = normalizeTimeOption(getTimePart(formData.expected_datetime));
    setFormData({ ...formData, expected_datetime: combineDateTime(dateKey, currentTime) });
    setCalendarOpen(false);
  };

  const setExpectedTime = (time) => {
    const currentDate = getDatePart(formData.expected_datetime) || toLocalDateKey(new Date());
    setFormData({ ...formData, expected_datetime: combineDateTime(currentDate, time) });
  };

  const clearExpectedDate = () => {
    setFormData({ ...formData, expected_datetime: "" });
    setCalendarOpen(false);
  };

  const toggleService = (service) => {
    setRequestForm((current) => {
      const selected = current.services.includes(service);
      return {
        ...current,
        services: selected
          ? current.services.filter((item) => item !== service)
          : [...current.services, service],
      };
    });
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(`Delete project request for ${project?.tail_number || "this tail"}? This cannot be undone.`);
    if (!confirmed) return;

    setSaving(true);
    setErrorText("");
    setMessage("");

    const { error } = await supabase
      .from("broker_intake")
      .delete()
      .eq("id", project?.broker_intake?.id);

    setSaving(false);

    if (error) {
      setErrorText(error.message || "Could not delete project.");
      return;
    }

    navigate(listPath, {
      state: { message: `${project?.tail_number || "Project"} was deleted.` },
    });
  };

  const intake = project?.broker_intake;
  const assignedPilotSelectOptions = formData.assigned_pilot && !assignedPilotOptions.includes(formData.assigned_pilot)
    ? [...assignedPilotOptions, formData.assigned_pilot]
    : assignedPilotOptions;
  const shootChecklistComplete = SHOOT_CHECKLIST_KEYS.every((key) =>
    checklist.find((item) => item.key === key)?.completed
  );
  const projectTimezone = project?.timezone || DEFAULT_TIMEZONE;
  const adminControlsDirty = !!project && (
    formData.current_status !== normalizeStatus(project.current_status) ||
    formData.expected_datetime !== toInputDateTime(project.expected_datetime, projectTimezone) ||
    (formData.timezone || DEFAULT_TIMEZONE) !== projectTimezone ||
    formData.assigned_pilot.trim() !== (project.assigned_pilot || "") ||
    formData.notes.trim() !== (project.notes || "")
  );

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
          <Link to={listPath} className="mb-2 inline-flex items-center gap-2 text-sm text-black/55 hover:text-black">
            <ArrowLeft className="h-4 w-4" />
            Admin Projects
          </Link>
          <h1 className="text-2xl font-extrabold tracking-tight">{project?.tail_number || "Project Detail"}</h1>
          <p className="text-xs text-black/60 mt-1">{formatDisplayName(intake?.broker_accounts?.name) || "Broker request"}</p>
          </div>
          {project && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
              Delete Request
            </button>
          )}
        </div>

        {loading ? (
          <div className={`${PANEL} p-6 text-sm text-black/60`}>Loading project…</div>
	        ) : errorText && !project ? (
	          <PortalFeedback type="error">{errorText}</PortalFeedback>
	        ) : (
          <>
	            {(message || errorText) && (
	              <PortalFeedback type={errorText ? "error" : "success"}>
	                {errorText || message}
	              </PortalFeedback>
	            )}

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
              <div className="space-y-6">
                <div className={`${PANEL} p-5`}>
                  <h2 className="text-lg font-bold">Admin Controls</h2>
                  <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Status</label>
                      <select value={formData.current_status} onChange={(e) => handleStatusChange(e.target.value)} className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm">
                        {PROJECT_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Assigned Pilot / Team</label>
                      <select
                        value={formData.assigned_pilot}
                        onChange={(e) => setFormData({ ...formData, assigned_pilot: e.target.value })}
                        className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">Unassigned</option>
                        {assignedPilotSelectOptions.map((pilot) => (
                          <option key={pilot} value={pilot}>{formatDisplayName(pilot)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-semibold mb-1">Expected By</label>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_130px_160px]">
                        <div className="relative">
                          <button
                            type="button"
                            onClick={openCalendar}
                            className="flex h-10 w-full items-center justify-between rounded-md border border-black/10 bg-white px-3 text-left text-sm hover:bg-black/[0.02]"
                          >
                            <span className={getDatePart(formData.expected_datetime) ? "text-black" : "text-black/40"}>
                              {formatDateDisplay(getDatePart(formData.expected_datetime))}
                            </span>
                            <CalendarDays className="h-4 w-4 text-black/55" />
                          </button>

                      {calendarOpen && (
                        <>
                          <button
                            type="button"
                            aria-label="Close calendar"
                            className="fixed inset-0 z-40 cursor-default bg-black/10"
                            onClick={() => setCalendarOpen(false)}
                          />
                          <div className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,360px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-black/10 bg-white p-4 shadow-2xl">
                            <div className="flex items-center justify-between">
                              <button
                                type="button"
                                onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-black/[0.04]"
                                aria-label="Previous month"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </button>
                              <p className="text-sm font-semibold">
                                {calendarMonth.toLocaleDateString([], { month: "long", year: "numeric" })}
                              </p>
                              <button
                                type="button"
                                onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-black/[0.04]"
                                aria-label="Next month"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="mt-3 grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wide text-black/45">
                              {["S", "M", "T", "W", "T", "F", "S"].map((day) => (
                                <div key={day} className="py-1">{day}</div>
                              ))}
                            </div>
                            <div className="mt-1 grid grid-cols-7 gap-1">
                              {getCalendarDays(calendarMonth).map((date) => {
                                const dateKey = toLocalDateKey(date);
                                const selected = dateKey === getDatePart(formData.expected_datetime);
                                const inMonth = date.getMonth() === calendarMonth.getMonth();
                                return (
                                  <button
                                    key={dateKey}
                                    type="button"
                                    onClick={() => setExpectedDate(dateKey)}
                                    className={`h-9 rounded-md text-sm font-medium transition ${
                                      selected
                                        ? "bg-[#EB040D] text-white"
                                        : inMonth
                                          ? "text-black hover:bg-black/[0.04]"
                                          : "text-black/30 hover:bg-black/[0.03]"
                                    }`}
                                  >
                                    {date.getDate()}
                                  </button>
                                );
                              })}
                            </div>

                            <div className="mt-4 flex items-center justify-between border-t border-black/10 pt-3">
                              <button type="button" onClick={clearExpectedDate} className="text-sm font-medium text-black/50 hover:text-black">
                                Clear
                              </button>
                              <button
                                type="button"
                                onClick={() => setExpectedDate(toLocalDateKey(new Date()))}
                                className="text-sm font-semibold text-[#EB040D] hover:opacity-80"
                              >
                                Today
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                        </div>

                        <select
                          value={normalizeTimeOption(getTimePart(formData.expected_datetime))}
                          onChange={(event) => setExpectedTime(event.target.value)}
                          className="h-10 rounded-md border border-black/10 bg-white px-3 text-sm"
                        >
                          {TIME_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>

                        <select
                          value={formData.timezone}
                          onChange={(event) => setFormData({ ...formData, timezone: event.target.value })}
                          className="h-10 rounded-md border border-black/10 bg-white px-3 text-sm"
                          aria-label="Expected by timezone"
                        >
                          {TIMEZONE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-semibold mb-1">Admin Notes</label>
                      <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={1} className="w-full rounded-md border border-black/10 px-3 py-2 text-sm" />
                    </div>
                    <div className="lg:col-span-2">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border px-4 text-sm font-semibold transition disabled:opacity-60 sm:w-auto ${
                          adminControlsDirty
                            ? "border-[#EB040D] bg-[#EB040D] text-white hover:opacity-90"
                            : "border-black/10 bg-white text-black hover:bg-black/[0.03]"
                        }`}
                      >
                        <Save className="h-4 w-4" />
                        {saving ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className={`${PANEL} overflow-hidden`}>
                  <div className="border-b border-black/10 px-5 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <ClipboardList className="h-5 w-5 text-[#EB040D]" />
                          <h2 className="text-lg font-bold tracking-tight">Broker Request</h2>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={toggleRequestEditing}
                        disabled={saving}
                        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-4 text-sm font-semibold hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                      >
                        {requestEditing ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                        {requestEditing ? "Cancel" : "Modify"}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 px-5 py-5 md:grid-cols-2 2xl:grid-cols-3">
                    <AdminField label="Tail Number">
                      <input
                        value={requestForm.tail_number}
                        onChange={(event) => setRequestForm({ ...requestForm, tail_number: event.target.value })}
                        readOnly={!requestEditing}
                        className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm read-only:bg-white/70 read-only:text-black/70"
                      />
                    </AdminField>
                    <AdminField label="Priority">
                      {requestEditing ? (
                        <PortalSelect
                          value={requestForm.priority}
                          placeholder="Select Priority"
                          options={priorityOptions}
                          onChange={(value) => setRequestForm({ ...requestForm, priority: value })}
                        />
                      ) : (
                        <p className="w-full rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm text-black/70">
                          {requestForm.priority || "—"}
                        </p>
                      )}
                    </AdminField>
                    <AdminField label="ICAO Location">
                      <input
                        value={requestForm.airport_identifier}
                        onChange={(event) => setRequestForm({ ...requestForm, airport_identifier: event.target.value })}
                        readOnly={!requestEditing}
                        className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm read-only:bg-white/70 read-only:text-black/70"
                      />
                    </AdminField>
                    <AdminField label="Point of Contact">
                      <input
                        value={requestEditing ? requestForm.contact_name : formatDisplayName(requestForm.contact_name)}
                        onChange={(event) => setRequestForm({ ...requestForm, contact_name: event.target.value })}
                        readOnly={!requestEditing}
                        className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm read-only:bg-white/70 read-only:text-black/70"
                      />
                    </AdminField>
                    <AdminField label="Contact Role">
                      {requestEditing ? (
                        <PortalSelect
                          value={requestForm.contact_role}
                          placeholder="Select role"
                          options={contactRoleOptions}
                          onChange={(value) => setRequestForm({ ...requestForm, contact_role: value })}
                        />
                      ) : (
                        <p className="w-full rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm text-black/70">
                          {requestForm.contact_role || "—"}
                        </p>
                      )}
                    </AdminField>
                    <AdminField label="Contact Phone">
                      {requestEditing ? (
                        <input
                          value={requestForm.contact_phone}
                          onChange={(event) => setRequestForm({ ...requestForm, contact_phone: event.target.value })}
                          className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
                        />
                      ) : (
                        <MobileContactAction
                          type="phone"
                          value={requestForm.contact_phone}
                          phoneActionsOpen={phoneActionsOpen}
                          onTogglePhoneActions={() => setPhoneActionsOpen((current) => !current)}
                          actionRef={phoneActionsRef}
                        />
                      )}
                    </AdminField>
                    <AdminField label="Contact Email">
                      {requestEditing ? (
                        <input
                          value={requestForm.contact_email}
                          onChange={(event) => setRequestForm({ ...requestForm, contact_email: event.target.value })}
                          className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
                        />
                      ) : (
                        <MobileContactAction type="email" value={requestForm.contact_email} />
                      )}
                    </AdminField>
                    <AdminField label="Contact Preference">
                      {requestEditing ? (
                        <PortalSelect
                          value={requestForm.contact_method_preference}
                          placeholder="No preference"
                          options={contactPreferenceOptions}
                          onChange={(value) => setRequestForm({ ...requestForm, contact_method_preference: value })}
                        />
	                      ) : (
	                        <p className="w-full rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm text-black/70">
	                          {getContactPreferenceLabel(requestForm.contact_method_preference)}
	                        </p>
	                      )}
                    </AdminField>
                    <AdminField label="Services" className="md:col-span-2 2xl:col-span-3">
                      {requestEditing ? (
                        <PortalServicesDropdown
                          selectedServices={requestForm.services}
                          onToggle={toggleService}
                        />
                      ) : (
                        <p className="w-full rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm leading-relaxed text-black/70">
                          {requestForm.services.length ? requestForm.services.join(", ") : "—"}
                        </p>
                      )}
                    </AdminField>
                    <AdminField label="Broker Notes" className="md:col-span-2 2xl:col-span-3">
                      <textarea
                        value={requestForm.notes}
                        onChange={(event) => setRequestForm({ ...requestForm, notes: event.target.value })}
                        rows={4}
                        readOnly={!requestEditing}
                        className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm read-only:bg-white/70 read-only:text-black/70"
                      />
                    </AdminField>
                  </div>

                  {requestEditing && (
                  <div className="flex justify-end border-t border-black/10 px-5 py-4">
                    <button
                      onClick={handleSaveBrokerRequest}
                      disabled={saving}
                      className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-[#EB040D] px-4 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60 sm:w-auto"
                      type="button"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? "Saving..." : "Save Broker Request"}
                    </button>
                  </div>
                  )}
                </div>

                <div className={`${PANEL} p-5`}>
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="min-w-0 text-lg font-bold">Photographer Checklist</h2>
                    <div className={`inline-flex w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
                      shootChecklistComplete ? "bg-[#EB040D]/10 text-[#EB040D]" : "bg-black/[0.04] text-black/55"
                    }`}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {shootChecklistComplete ? "Shoot completed" : "Shoot in progress"}
                    </div>
                  </div>
                  <div className="mt-4 space-y-3">
                    {checklist.map((item) => {
                      const itemSaving = savingChecklistKey === item.key;
                      const itemDirty = hasChecklistChanges(item);
                      return (
                        <div key={item.key} className={`rounded-xl border bg-white/70 p-3 transition sm:p-4 ${
                          itemDirty ? "border-[#EB040D]/45 shadow-[0_0_0_1px_rgba(235,4,13,0.18)]" : "border-black/10"
                        }`}>
                          <div className="flex items-start justify-between gap-3">
                            <label className="flex min-w-0 items-start gap-3">
                              <input
                                type="checkbox"
                                checked={item.completed}
                                disabled={itemSaving || saving}
                                onChange={(event) => updateChecklistItem(item.key, { completed: event.target.checked })}
                                className="mt-1 h-4 w-4 shrink-0 accent-[#EB040D]"
                              />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-[#1A1A1A]">{item.label}</span>
                              </span>
                            </label>
                            <span className="inline-flex w-fit shrink-0 rounded-full bg-black/[0.04] px-2.5 py-1 text-xs font-medium text-black/55">
                              {item.notifies}
                            </span>
                          </div>
                          <div className="mt-2">
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-black/45">
                              Notes
                            </label>
                            <textarea
                              value={item.notes}
                              onChange={(event) => {
                                const notes = event.target.value;
                                updateChecklistItem(item.key, { notes });
                              }}
                              rows={1}
                              className="min-h-11 w-full resize-y rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
                              placeholder="Add internal note..."
                            />
                            {itemDirty && (
                              <div className="mt-2 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleSaveChecklistItem(item.key)}
                                  disabled={itemSaving || saving}
                                  className={`inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border px-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto ${
                                    itemDirty
                                      ? "border-[#EB040D] bg-[#EB040D] text-white hover:opacity-90"
                                      : "border-black/10 bg-white text-black hover:bg-black/[0.03]"
                                  }`}
                                >
                                  <Save className="h-4 w-4" />
                                  {itemSaving ? "Saving..." : itemDirty ? "Save Changes" : "Save Note"}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className={`${PANEL} p-5`}>
                  <h2 className="text-lg font-bold">Status History</h2>
                  <div className="mt-4 space-y-3">
                    {history.length === 0 && <p className="text-sm text-black/55">No history yet.</p>}
                    {history.map((item) => (
                      <div key={item.id} className="flex gap-3 rounded-xl border border-black/10 bg-white/70 p-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#EB040D]" />
                        <div>
                          <p className="text-sm font-semibold">
                            {formatStatusTransition(item)}
                          </p>
                          {formatHistoryExpectedTiming(item, project?.timezone) && (
                            <p className="text-xs font-medium text-black/70">
                              Expected: {formatHistoryExpectedTiming(item, project?.timezone)}
                            </p>
                          )}
                          <p className="text-xs text-black/50">{new Date(item.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`${PANEL} p-5`}>
                  <h2 className="text-lg font-bold">Request Change Log</h2>
                  <div className="mt-4 space-y-3">
                    {changeLog.length === 0 && <p className="text-sm text-black/55">No broker request changes yet.</p>}
                    {changeLog.map((item) => {
                      const diffs = item.change_type === "update"
                        ? formatBrokerChangeDiffs(item.old_values, item.new_values)
                        : [];
                      const actorRole = item.actor_type === "admin" ? "Admin" : item.actor_type === "broker" ? "Broker" : "User";
                      const actorName = formatDisplayName(item.actor_name) || item.actor_email || actorRole;
                      const actionLabel = item.change_type === "delete" ? "deleted" : "updated";

                      return (
                        <div key={item.id} className="rounded-xl border border-black/10 bg-white/70 p-3">
                          <p className="text-sm font-semibold">
                            {actorName} {actionLabel} this request
                          </p>
                          <p className="mt-1 text-xs font-medium text-black/45">{actorRole}</p>
                          {diffs.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {diffs.map((diff) => (
                                <div key={diff.key} className="rounded-lg border border-black/5 bg-black/[0.015] px-3 py-2">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-black/45">{diff.label}</p>
                                  <p className="mt-1 text-sm text-black/75">
                                    <span className="break-words">{diff.before}</span>
                                    <span className="px-2 text-black/35">→</span>
                                    <span className="break-words font-semibold text-black/85">{diff.after}</span>
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                          {item.change_type === "update" && diffs.length === 0 && (
                            <p className="mt-2 text-sm text-black/55">No field-level changes were recorded for this log entry.</p>
                          )}
                          {item.change_type === "delete" && (
                            <p className="mt-2 text-sm text-black/65">{actorName} deleted this request.</p>
                          )}
                          <p className="mt-3 text-xs text-black/50">{new Date(item.created_at).toLocaleString()}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminDashboardLayout>
  );
}

function AdminField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-sm font-semibold">{label}</label>
      {children}
    </div>
  );
}

function MobileContactAction({
  type,
  value,
  phoneActionsOpen = false,
  onTogglePhoneActions = () => {},
  actionRef = null,
}) {
  const displayValue = value || "—";
  const dialablePhone = type === "phone" ? getDialablePhone(value) : "";
  const mailtoEmail = type === "email" ? getMailtoEmail(value) : "";
  const canUseAction = type === "phone" ? Boolean(dialablePhone) : Boolean(mailtoEmail);
  const Icon = type === "phone" ? Phone : Mail;

  return (
    <div ref={actionRef} className="relative">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm text-black/70 sm:w-full">
          {displayValue}
        </p>
        {type === "email" ? (
          <a
            href={mailtoEmail ? `mailto:${mailtoEmail}` : undefined}
            aria-label="Email contact"
            className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border text-sm sm:hidden ${
              canUseAction
                ? "border-[#EB040D]/20 bg-[#EB040D] text-white shadow-sm"
                : "pointer-events-none border-black/10 bg-black/5 text-black/30"
            }`}
          >
            <Icon className="h-4 w-4" />
          </a>
        ) : (
          <button
            type="button"
            onClick={canUseAction ? onTogglePhoneActions : undefined}
            disabled={!canUseAction}
            aria-expanded={phoneActionsOpen}
            aria-label="Contact phone actions"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#EB040D]/20 bg-[#EB040D] text-sm text-white shadow-sm disabled:border-black/10 disabled:bg-black/5 disabled:text-black/30 sm:hidden"
          >
            <Icon className="h-4 w-4" />
          </button>
        )}
      </div>

      {type === "phone" && phoneActionsOpen && dialablePhone && (
        <div className="absolute right-0 z-20 mt-2 w-44 overflow-hidden rounded-xl border border-black/10 bg-white shadow-[0_18px_40px_rgba(0,0,0,0.18)] sm:hidden">
          <a
            href={`tel:${dialablePhone}`}
            className="flex items-center gap-3 border-b border-black/10 px-4 py-3 text-sm font-semibold text-black hover:bg-[#EB040D]/5"
          >
            <Phone className="h-4 w-4 text-[#EB040D]" />
            Call
          </a>
          <a
            href={`sms:${dialablePhone}`}
            className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-black hover:bg-[#EB040D]/5"
          >
            <MessageSquare className="h-4 w-4 text-[#EB040D]" />
            Text
          </a>
        </div>
      )}
    </div>
  );
}
